// tslint:disable:object-literal-sort-keys max-classes-per-file
/**
 * @file
 *
 * React component that generates a basic Table
 *
 * @TODO: Good candidate for more thorough refactor
 */

import * as React from 'react';

// Utils
import { checkForCutline, machineName } from '../../util/util';

// Components
import CutlineText from '../CutlineText/CutlineText';

// Types
import { cutlineContext, CutlineRow } from '../../types';

/**
 *
 * @param {string} item
 * @constructor
 */
interface IHeaderItemProps {
  item: string;
}

const rawHeader = (text: string) => ({ __html: text });
class HeaderItem extends React.PureComponent<IHeaderItemProps> {
  public render() {
    return (
      <th
        className={machineName(this.props.item)}
        data-testid="table-header-cell"
      >
        <span dangerouslySetInnerHTML={rawHeader(this.props.item)} />
      </th>
    );
  }
}

interface IHeaderRowProps {
  row: string[];
}

/**
 *
 * @param {string[]} row
 * @constructor
 */
class HeaderRow extends React.PureComponent<IHeaderRowProps> {
  public render() {
    const rowItems = this.props.row.map(item => (
      <HeaderItem item={item} key={machineName(item)} />
    ));
    return <tr data-testid="table-header-row">{rowItems}</tr>;
  }
}

interface IBodyItemProps {
  // Table data cell
  item: any;
  // Array of Strings describing header rows
  headerRows: string[];
  // Indexed position
  index: number;
}

/**
 * Table cell (<td/>) component
 */
class BodyItem extends React.PureComponent<IBodyItemProps> {
  public render() {
    const { item, headerRows, index } = this.props;
    return (
      <td
        className={machineName(headerRows[index])}
        data-label={headerRows[index]}
        data-testid="table-cell"
      >
        {item}
      </td>
    );
  }
}

interface IBodyRowProps {
  // Array of individual cells of table data
  row: any[];
  // Array of header titles
  headerRows: string[];
}

/**
 * Table row (<tr/>) component
 */
class BodyRow extends React.PureComponent<IBodyRowProps> {
  public render() {
    const { row, headerRows } = this.props;
    const rowItems = row.map((item, index) => (
      <BodyItem item={item} headerRows={headerRows} index={index} key={index} />
    ));

    return <tr data-testid="table-row">{rowItems}</tr>;
  }
}

interface ITableProps {
  // An array of table rows that are too varied to type
  tableData: any[];
  // Hide/show the header
  showHeader?: boolean;
  // Striped rows
  smartStyles?: boolean;
  // Use to make tables unique so that you can target a unique table's class to set custom styles
  tableIdentifier?: string;
}

/**
 * Creates a custom cutline row.
 * @param cutlineText
 * @param key
 */
const cutlineRow: CutlineRow = (cutlineText: string, key: string) => (
  <tr key={key} className="cutline" data-testid="cutline-row">
    <td colSpan={8}>
      <CutlineText text={cutlineText} />
    </td>
  </tr>
);

/**
 * Table component
 */
class Table extends React.PureComponent<ITableProps> {
  public static defaultProps = {
    tableIdentifier: 'default',
    showHeader: true,
    smartStyles: true,
    tableData: [
      {
        'Head 1': 'Row 1 - Item 1',
        'Head 2': 'Row 1 - Item 2',
      },
      {
        'Head 1': 'Row 2 - Item 1',
        'Head 2': 'Row 2 - Item 2',
      },
    ],
  };

  public render() {
    const { smartStyles, showHeader, tableData } = this.props;

    // Header rows for table.
    const headerRows = Object.keys(tableData[0]);
    // Body rows for table.
    const bodyRows = tableData.map((row, index) => {
      const rowItems = Object.values(row);

      return <BodyRow row={rowItems} key={index} headerRows={headerRows} />;
    });

    return (
      <cutlineContext.Consumer>
        {({ cutlines }) => (
          <table
            className={smartStyles ? 'smart-table' : ''}
            data-testid="table-table"
          >
            {showHeader && (
              <thead>
                <HeaderRow row={headerRows} />
              </thead>
            )}
            <tbody>{checkForCutline(bodyRows, cutlineRow, cutlines)}</tbody>
          </table>
        )}
      </cutlineContext.Consumer>
    );
  }
}

export default Table;
